import React, { FC, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Portal,
  Typography,
} from '@material-ui/core';
import { Formik, FormikProps } from 'formik';
import { object, string } from 'yup';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import classNames from 'classnames';

import { ValidatedTextField } from 'components';
import { ILink } from '../../types';

interface IProps extends WithStyles<typeof styles> {
  open: boolean;
  onClose: () => void;
  onSave: (values: ILink) => void;
  onRemove: () => void;
  initialValues: ILink;
}

const styles = () =>
  createStyles({
    content: {
      minWidth: 300,
    },
    actions: {
      marginTop: 0,
    },
    remove: {
      justifyContent: 'space-between',
    },
  });

const LinkButton: FC<IProps> = props => {
  const { classes, open, initialValues, onClose, onSave, onRemove } = props;
  const { t } = useTranslation();

  const showRemove = !!initialValues.url.length;
  const actionsClasses = classNames(classes.actions, {
    [classes.remove]: showRemove,
  });

  const LinkSchema = object().shape({
    url: string()
      .url(t('messages:linkUrlInvalid'))
      .required(t('messages:linkUrlError')),
  });

  function handleSave(values: ILink, formikProps: FormikProps<ILink>) {
    formikProps.resetForm();
    onSave(values);
  }

  function onSubmit(event: SyntheticEvent, handleSubmit: FormikProps<ILink>['handleSubmit']) {
    event.preventDefault();
    event.stopPropagation();

    handleSubmit();
  }

  return (
    <Formik
      enableReinitialize
      onSubmit={handleSave}
      initialValues={initialValues}
      validationSchema={LinkSchema}>
      {({ handleSubmit }) => (
        <Portal>
          <Dialog open={open} onClose={onClose}>
            <form onSubmit={event => onSubmit(event, handleSubmit)}>
              <DialogTitle disableTypography>
                <Typography variant="subtitle2">{t('messages:linkInsertEdit')}</Typography>
                <IconButton onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent className={classes.content}>
                <ValidatedTextField
                  persistLabel
                  name="url"
                  i18nKey="common:url"
                  TextFieldProps={{ margin: 'none' }}
                />
              </DialogContent>
              <DialogActions className={actionsClasses}>
                {showRemove && (
                  <Button type="button" onClick={onRemove}>
                    {t('components:removeLink')}
                  </Button>
                )}
                <Button type="submit" color="primary" variant="contained" size="large">
                  {t('common:save')}
                </Button>
              </DialogActions>
            </form>
          </Dialog>
        </Portal>
      )}
    </Formik>
  );
};

export default withStyles(styles)(LinkButton);
